import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import Api from "../../Api/Api.json";
import moment from "moment";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid2,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useQueryClient } from "@tanstack/react-query";

// Define sections with headers and questions (immutable)
const SECTIONS = {
  anemia: {
    header: "রক্তশূন্যতা (Anemia)",
    q1: "আপনি কি অস্বাভাবিকভাবে ক্লান্ত বা দুর্বল বোধ করছেন, এমনকি বিশ্রামের পরেও?",
    q2: "সাধারণ কাজ করার সময় কি আপনার শ্বাস নিতে কষ্ট হয়?",
    q3: "আপনি কি সম্প্রতি ত্বক, মাড়ি বা নখ ফ্যাকাসে হয়ে গেছে বলে মনে করছেন?",
    q4: "আপনি কি মাথা ঘোরা, মাথাব্যথা বা হৃদস্পন্দন বেড়ে যাওয়ার অভিজ্ঞতা পেয়েছেন?",
  },
  hypertension: {
    header: "উচ্চ রক্তচাপজনিত ব্যাধি (Hypertensive Disorders)",
    q1: "আপনি কি মারাত্মক বা স্থায়ী মাথাব্যথা অনুভব করেছেন?",
    q2: "আপনি কি আপনার হাত, মুখ, বা পায়ে স্থায়ী ফোলাভাব লক্ষ্য করেছেন?",
    q3: "আপনি কি ঝাপসা দৃষ্টি, আলোতে সংবেদনশীলতা, বা চোখে দাগ দেখতে পাচ্ছেন?",
    q4: "আপনি কি আপনার পেটের উপরিভাগে বা পাঁজরের নিচে ব্যথা অনুভব করেছেন?",
  },
  diabetes: {
    header: "ডায়াবেটিস সম্পর্কিত জটিলতা (Diabetes-related Complications)",
    q1: "আপনি কি অতিরিক্ত পিপাসা বা বারবার প্রস্রাব করার অভিজ্ঞতা পেয়েছেন?",
    q2: "আপনি কি খাবারের পরে অস্বাভাবিকভাবে ক্লান্ত বা অলস বোধ করছেন?",
    q3: "আপনি কি সম্প্রতি উল্লেখযোগ্য ওজন বেড়েছে বা হঠাৎ ওজন পরিবর্তন লক্ষ্য করেছেন?",
    q4: "আপনি কি আগে গর্ভকালীন ডায়াবেটিসের অভিজ্ঞতা পেয়েছেন বা একটি বড় শিশুর জন্ম দিয়েছেন?",
  },
  cardiac_disease: {
    header: "হৃদরোগ (Cardiac Disease)",
    q1: "আপনি কি বুকে ব্যথা, চাপ, বা হৃদস্পন্দন অনুভব করছেন?",
    q2: "আপনি কি হালকা কাজে বা শোয়া অবস্থায় শ্বাসকষ্ট বোধ করেন?",
    q3: "আপনি কি মাথা ঘোরা বা অজ্ঞান হওয়ার অভিজ্ঞতা পেয়েছেন?",
    q4: "আপনার কি হৃদরোগ বা পারিবারিক ইতিহাসে হৃদরোগ ছিল?",
  },
  preeclampsia: {
    header: "প্রিইক্লাম্পসিয়া (Preeclampsia)",
    q1: "আপনি কি আপনার হাত, মুখ, বা পায়ে হঠাৎ ফোলাভাব অনুভব করেছেন?",
    q2: "আপনি কি আগে গর্ভধারণে উচ্চ রক্তচাপের অভিজ্ঞতা পেয়েছেন?",
    q3: "আপনি কি এমন মাথাব্যথা অনুভব করছেন যা সাধারণ ওষুধে দূর হয় না?",
    q4: "আপনি কি প্রস্রাব কম বা প্রস্রাবে পরিবর্তন লক্ষ্য করেছেন?",
  },
  uti: {
    header: "মূত্রনালীর সংক্রমণ (UTI)",
    q1: "প্রস্রাব করার সময় কি জ্বালা বা ব্যথা অনুভব করেন?",
    q2: "আপনি কি প্রস্রাবের ফ্রিকোয়েন্সি বা তাড়াহুড়ো বাড়তে দেখেছেন?",
    q3: "আপনার কি তলপেট বা পিঠে ব্যথা হচ্ছে?",
    q4: "আপনি কি ঘোলাটে, দুর্গন্ধযুক্ত বা রক্তমিশ্রিত প্রস্রাব লক্ষ্য করেছেন?",
  },
  liver_disease: {
    header: "লিভারের রোগ (Liver Disease)",
    q1: "আপনি কি বিশেষত হাত ও পায়ে তীব্র চুলকানি অনুভব করছেন?",
    q2: "আপনি কি গাঢ় প্রস্রাব বা ফ্যাকাসে মল লক্ষ্য করেছেন?",
    q3: "আপনি কি ক্রমাগত বমি বমি ভাব বা ক্ষুধামন্দা অনুভব করছেন?",
    q4: "আপনার কি পাঁজরের নিচে ডানদিকে ব্যথা হচ্ছে?",
  },
  coagulation_disorder: {
    header: "রক্ত জমাট বাঁধার ব্যাধি (Coagulation Disorder)",
    q1: "আপনার কি কোনো অস্বাভাবিক বা অতিরিক্ত রক্তক্ষরণ হয়েছে (যেমন মাড়ি বা নাক)?",
    q2: "আপনি কি শরীরে আঘাত ছাড়াই আঘাতের দাগ লক্ষ্য করেছেন?",
    q3: "আপনি কি রক্ত জমাট বাঁধার সমস্যা বা রক্ত জমাট বাঁধার অভিজ্ঞতা পেয়েছেন?",
    q4: "আপনার পরিবারের কি রক্ত জমাট বাঁধার ইতিহাস আছে?",
  },
  placenta_previa: {
    header: "প্লাসেন্টা প্রিভিয়ার (Placenta Previa)",
    q1: "আপনি কি এই গর্ভাবস্থায় যোনি থেকে রক্তপাত লক্ষ্য করেছেন?",
    q2: "আপনি কি আগের গর্ভধারণে প্লাসেন্টা প্রিভিয়ার অভিজ্ঞতা পেছেন?",
    q3: "আলট্রাসাউন্ডে কি প্লাসেন্টা নীচে থাকার কথা বলা হয়েছে?",
    q4: "আপনার কি পেলভিক অঞ্চলে চাপ বা অস্বস্তি অনুভব করছেন?",
  },
  thyroid_disorder: {
    header: "থাইরোইড ব্যাধি (Thyroid Disorder)",
    q1: "আপনি কি অস্বাভাবিক ক্লান্তি বা অবসন্নতা অনুভব করছেন?",
    q2: "আপনার কি ওজন বেড়ে বা কমেছে, এবং এটি ব্যাখ্যাতীত?",
    q3: "আপনি কি অন্যদের তুলনায় অস্বাভাবিকভাবে ঠান্ডা বা গরম বোধ করছেন?",
    q4: "আপনি কি চুল পড়া, শুষ্ক ত্বক বা হৃদস্পন্দনের পরিবর্তন লক্ষ্য করেছেন?",
  },
  antepartum_hemorrhage: {
    header: "গর্ভকালীন রক্তক্ষরণ (Antepartum Hemorrhage)",
    q1: "আপনি কি এই গর্ভাবস্থায় যোনি থেকে রক্তপাত অনুভব করেছেন?",
    q2: "রক্তপাতের সময় কি আপনার পেটে ব্যথা বা ক্র্যাম্প হয়?",
    q3: "আপনার কি সম্প্রতি পেটের কোনো আঘাত বা চোট হয়েছে?",
    q4: "আগের গর্ভাবস্থায় কি রক্তপাতের সমস্যা ছিল?",
  },
  amniotic_fluid: {
    header: "আমনিওটিক ফ্লুইড (Amniotic Fluid)",
    q1: "আপনি কি যোনি থেকে কোনো তরল লিক হচ্ছে বলে মনে করছেন?",
    q2: "আলট্রাসাউন্ডে কি অ্যামনিয়োটিক ফ্লুইডের স্তর অস্বাভাবিক বলা হয়েছে?",
    q3: "আপনি কি আপনার শিশুর অস্বাভাবিকভাবে বড় বা ছোট নড়াচড়া অনুভব করছেন?",
    q4: "আপনার কি আগের গর্ভাবস্থায় অল্প বা অতিরিক্ত অ্যামনিয়োটিক ফ্লুইডের অভিজ্ঞতা ছিল?",
  },
};

export default function RiskFactorIntake({
  patient_id,
  setSelectedDate,
  setIsIntake,
}) {
  const queryClient = useQueryClient();
  const topRef = useRef(null);
  const totalSteps = 4;
  const sectionKeys = Object.keys(SECTIONS);
  const sectionsPerStep = Math.ceil(sectionKeys.length / totalSteps);
  const [activeStep, setActiveStep] = useState(0);

  // Effect to scroll to top when activeStep changes
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeStep]);

  // Build defaultValues so all radio groups start controlled
  const defaultValues = sectionKeys.reduce((acc, key) => {
    acc[key] = { a1: "", a2: "", a3: "", a4: "" };
    return acc;
  }, {});

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({ defaultValues });

  const onSubmit = (values) => {
    const query_dict = {};
    sectionKeys.forEach((key) => {
      const section = SECTIONS[key];
      query_dict[key] = {
        ...section,
        a1: values[key]?.a1 || "",
        a2: values[key]?.a2 || "",
        a3: values[key]?.a3 || "",
        a4: values[key]?.a4 || "",
      };
    });

    const requestData = {
      task: "submitRiskFactorDataForm",
      query_dict,
      patient_id,
      creation_time: moment().format("YYYY-MM-DD HH:mm:ss"),
    };

    axios
      .post(Api.PREGNANCY_DATA_API, requestData, {
        headers: { "Content-Type": "application/json" },
      })
      .then((result) => {
        if (result.data) {
          toast.success("ফর্ম সফলভাবে জমা হয়েছে");
          setTimeout(() => {
            queryClient.invalidateQueries(["riskFactorDates", patient_id]);
            setSelectedDate(null);
            setIsIntake(false);
          }, 300);
        }
      })
      .catch((err) => {
        toast.error("জমা দিতে সমস্যা হয়েছে। আবার চেষ্টা করুন");
      });
  };

  const currentKeys = sectionKeys.slice(
    activeStep * sectionsPerStep,
    activeStep * sectionsPerStep + sectionsPerStep
  );

  const handleNext = async (event) => {
    event.preventDefault();
    const fields = currentKeys.flatMap((key) =>
      [1, 2, 3, 4].map((i) => `${key}.a${i}`)
    );
    const valid = await trigger(fields);
    if (valid) {
      setActiveStep((s) => s + 1);
      // No need for explicit scrollTo here as useEffect will handle it
    }
  };

  const handleBack = (event) => {
    event.preventDefault();
    setActiveStep((s) => s - 1);
    // No need for explicit scrollTo here as useEffect will handle it
  };

  return (
    <>
      {/* Add a ref to the top of the component */}
      <div ref={topRef}></div>
      <Container
        maxWidth="md"
        sx={{
          py: 4,
          px: { xs: 1, sm: 2 }, // Reduce padding on mobile
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ color: "#1976d2", fontWeight: 600 }}
        >
          ঝুঁকি ফ্যাক্টর
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ mb: 4, px: { xs: 1, sm: 0 } }}
        >
          {Array.from({ length: totalSteps }).map((_, idx) => (
            <Step key={idx}>
              <StepLabel>Step {idx + 1}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid2
            container
            spacing={4}
            justifyContent="center"
            direction="column"
          >
            {currentKeys.map((key) => {
              const section = SECTIONS[key];
              return (
                <Grid2 item key={key}>
                  <Paper
                    elevation={3}
                    sx={{
                      mx: "auto",
                      p: { xs: 2, sm: 3 }, // Reduce padding on mobile
                      width: "100%", // Always full width within container
                      maxWidth: { xs: "100%", sm: 700 }, // Max width only on larger screens
                      backgroundColor: "#fafafa", // off-white card background
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: "#1976d2", fontWeight: 500 }}
                    >
                      {section.header}
                    </Typography>
                    {[1, 2, 3, 4].map((idx) => (
                      <Box mb={2} key={idx}>
                        <FormControl
                          component="fieldset"
                          error={Boolean(errors[key]?.[`a${idx}`])}
                        >
                          <FormLabel
                            component="legend"
                            sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                          >
                            {idx}. {section[`q${idx}`]}
                          </FormLabel>
                          <Controller
                            name={`${key}.a${idx}`}
                            control={control}
                            rules={{ required: "এই প্রশ্নের উত্তর দিতে হবে" }}
                            render={({ field }) => (
                              <RadioGroup
                                row={false} // Stack vertically on mobile for better space
                                {...field}
                                sx={{
                                  flexDirection: { xs: "column", sm: "row" },
                                }}
                              >
                                {["হ্যা", "না", "জানি না"].map((opt) => (
                                  <FormControlLabel
                                    key={opt}
                                    value={opt}
                                    control={<Radio />}
                                    label={opt}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: { xs: "0.9rem", sm: "1rem" },
                                      },
                                    }}
                                  />
                                ))}
                              </RadioGroup>
                            )}
                          />
                          {errors[key]?.[`a${idx}`] && (
                            <FormHelperText>
                              {errors[key][`a${idx}`].message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    ))}
                  </Paper>
                </Grid2>
              );
            })}

            <Grid2 item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                mt={4}
                width="100%" // Always full width
                maxWidth={{ xs: "100%", sm: 700 }} // Max width only on larger screens
                mx="auto"
              >
                <Button
                  type="button"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="contained"
                  sx={{
                    backgroundColor: "#e0e0e0",
                    color: "#333333",
                    "&:hover": { backgroundColor: "#d5d5d5" },
                  }}
                >
                  Previous
                </Button>

                {activeStep < totalSteps - 1 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    variant="contained"
                  >
                    Next
                  </Button>
                ) : (
                  <Button type="submit" variant="contained">
                    Submit
                  </Button>
                )}
              </Box>
            </Grid2>
          </Grid2>
        </form>
      </Container>
    </>
  );
}
